import { Star } from 'lucide-react'
import type { AuditReport } from '@/lib/api/amazon-listing'
const STAR_COLORS: Record<string, string> = {
  '5': 'bg-green-500', '4': 'bg-green-400', '3': 'bg-yellow-400', '2': 'bg-orange-400', '1': 'bg-red-500',
}

export function OverviewCard({ overview, t }: { overview: AuditReport['review_insights']['overview']; t: (k: string, opts?: Record<string, string>) => string }) {
  if (!overview) return null
  const rawPos = overview.sentiment_ratio?.positive ?? 0
  const rawNeg = overview.sentiment_ratio?.negative ?? 0
  const pos = rawPos <= 1 ? rawPos * 100 : rawPos
  const neg = rawNeg <= 1 ? rawNeg * 100 : rawNeg
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5">
      <h3 className="font-semibold text-neutral-900 dark:text-white mb-3">{t('amazonListing.report.reviewInsights')}</h3>
      <div className="flex items-center gap-6 text-sm">
        <div className="flex items-center gap-1.5">
          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{overview.average_rating?.toFixed(1)}</span>
          <span className="text-neutral-500">({overview.total_reviews} {t('amazonListing.report.reviews')})</span>
        </div>
        <div className="flex-1 max-w-48 h-2 rounded-full overflow-hidden flex bg-neutral-200 dark:bg-neutral-600">
          <div className="bg-green-500 h-full" style={{ width: `${pos}%` }} />
          <div className="bg-red-500 h-full" style={{ width: `${neg}%` }} />
        </div>
      </div>
    </div>
  )
}

export function StarDistribution({ dist }: { dist: Record<string, number> }) {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5">
      <div className="space-y-2">
        {['5', '4', '3', '2', '1'].map((star) => (
          <div key={star} className="flex items-center gap-3 text-sm">
            <span className="w-12 text-neutral-600 dark:text-neutral-400">{star} ★</span>
            <div className="flex-1 h-3 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${STAR_COLORS[star]}`} style={{ width: `${dist[star] ?? 0}%` }} />
            </div>
            <span className="w-10 text-right text-neutral-600 dark:text-neutral-400">{dist[star] ?? 0}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function AspectCards({ aspects, t }: { aspects: AuditReport['review_insights']['aspects']; t: (k: string, opts?: Record<string, string>) => string }) {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5">
      <h4 className="font-medium text-sm mb-3">{t('amazonListing.report.reviewAspects')}</h4>
      <div className="space-y-3">
        {aspects.map((a, i) => (
          <div key={i} className="border-b last:border-0 border-neutral-100 dark:border-neutral-700 pb-2 last:pb-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium">{a.aspect}</span>
              <span className="px-1.5 py-0.5 rounded text-xs bg-neutral-100 dark:bg-neutral-700">{t(`amazonListing.report.class${a.classification.replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase()).replace(/^./, (c: string) => c.toUpperCase())}`, { defaultValue: a.classification })}</span>
              <span className="text-xs text-neutral-500 ml-auto">{a.mention_count} {t('amazonListing.report.mentions')}</span>
            </div>
            <div className="flex h-1.5 rounded-full overflow-hidden bg-neutral-200 dark:bg-neutral-600 mb-1">
              <div className="bg-green-500" style={{ width: `${(a.sentiment_ratio ?? 0) * 100}%` }} />
              <div className="bg-red-500" style={{ width: `${(1 - (a.sentiment_ratio ?? 0)) * 100}%` }} />
            </div>
            {a.sample_quotes?.positive && (
              <p className="text-xs italic text-neutral-500 dark:text-neutral-400 border-l-2 border-neutral-300 pl-2 mt-1 truncate">{a.sample_quotes.positive}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export function Improvements({ items, t }: { items: AuditReport['review_insights']['product_improvements']; t: (k: string, opts?: Record<string, string>) => string }) {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <h4 className="text-sm font-medium mb-2">{t('amazonListing.report.productImprovements')}</h4>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">{t(`amazonListing.report.freq${item.frequency.charAt(0).toUpperCase()}${item.frequency.slice(1)}`, { defaultValue: item.frequency })}</span>
            <span>{item.suggested_fix}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function CompetitorComparison({ data, t }: { data: AuditReport['review_insights']['competitor_comparison']; t: (k: string, opts?: Record<string, string>) => string }) {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <h4 className="text-sm font-medium mb-2">{t('amazonListing.report.competitorComparison')}</h4>
      <div className="flex items-center gap-6 text-sm">
        <span>{t('amazonListing.report.sourceRating')}: <strong>{data.source_rating?.toFixed(1)}</strong></span>
        <span>{t('amazonListing.report.marketAvgRating')}: <strong>{data.market_avg_rating?.toFixed(1)}</strong></span>
      </div>
    </div>
  )
}
